import { useMemo, useState } from "react";
import { Expand, Image as ImageIcon, ImageOff } from "lucide-react";
import FormModal, { BtnSecondary } from "../../../../components/erp/FormModal";
import { getImageUrl } from "../../../../services/api";
import ImageDropzone from "../ImageDropzone";
import { makeVariantKey } from "../utils/variantMatrix";

function variantLabel(variant = {}) {
  const values = Object.values(variant.optionValues || {}).filter(Boolean);
  return values.length ? values.join(" / ") : variant.sku || "Variant";
}

function statusPillClass(ok) {
  return ok
    ? "border-emerald-200 bg-white text-emerald-800 dark:border-emerald-900/40 dark:bg-transparent dark:text-emerald-200"
    : "border-amber-200 bg-white text-amber-800 dark:border-amber-900/40 dark:bg-transparent dark:text-amber-200";
}

export default function StepMediaAssets({ state, setState }) {
  const media = state.media || {};
  const images = useMemo(() => media.productImages || [], [media.productImages]);
  const variants = useMemo(() => state?.variants || [], [state?.variants]);
  const coverImage = images[0] || null;

  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [replaceVariantKey, setReplaceVariantKey] = useState("");
  const [previewState, setPreviewState] = useState(null);
  const [previewBroken, setPreviewBroken] = useState(false);

  const variantAssignments = useMemo(
    () =>
      variants.map((variant) => ({
        variantKey: makeVariantKey(variant.optionValues || {}),
        label: variantLabel(variant),
        imageUrl: variant.imageUrl || null,
      })),
    [variants]
  );

  const assignedVariantCount = variantAssignments.filter((entry) => entry.imageUrl).length;
  const missingVariantCount = variantAssignments.length - assignedVariantCount;
  const mediaOk = images.length > 0 && (variantAssignments.length === 0 || missingVariantCount === 0);

  const addProductImages = (urls) => {
    setState((prev) => ({
      ...prev,
      media: {
        ...prev.media,
        productImages: [...(prev.media?.productImages || []), ...urls],
      },
    }));
  };

  const removeImage = (url) => {
    setState((prev) => ({
      ...prev,
      media: {
        ...prev.media,
        productImages: (prev.media?.productImages || []).filter((value) => value !== url),
      },
    }));
  };

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    setState((prev) => {
      const nextImages = [...(prev.media?.productImages || [])];
      if (!nextImages[from] || to < 0 || to >= nextImages.length) return prev;
      const [item] = nextImages.splice(from, 1);
      nextImages.splice(to, 0, item);
      return {
        ...prev,
        media: {
          ...prev.media,
          productImages: nextImages,
        },
      };
    });
  };

  const replaceAt = (idx, url) => {
    if (!url) return;
    setState((prev) => {
      const nextImages = [...(prev.media?.productImages || [])];
      if (!nextImages[idx]) return prev;
      nextImages[idx] = url;
      return {
        ...prev,
        media: {
          ...prev.media,
          productImages: nextImages,
        },
      };
    });
  };

  const setVariantImage = (variantKey, url) => {
    if (!variantKey) return;
    setState((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((variant) =>
        makeVariantKey(variant.optionValues || {}) === variantKey
          ? { ...variant, imageUrl: url || null }
          : variant
      ),
    }));
  };

  const openPreview = ({
    title,
    src,
    description = "",
    emptyLabel = "No image assigned yet.",
  }) => {
    setPreviewBroken(false);
    setPreviewState({
      title,
      src: src || null,
      description,
      emptyLabel,
    });
  };

  return (
    <div className="space-y-5">
      <div
        className={
          "rounded-2xl border p-4 " +
          (mediaOk
            ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/40"
            : "border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/40")
        }
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Media
            </div>
            <div className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
              Manage the cover image, gallery ordering, and per-variant image assignments here.
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-zinc-600 dark:text-zinc-300">
              <span className="rounded-full border border-white/70 bg-white/80 px-2.5 py-1 font-medium dark:border-white/10 dark:bg-black/10">
                {images.length} product image{images.length === 1 ? "" : "s"}
              </span>
              <span className="rounded-full border border-white/70 bg-white/80 px-2.5 py-1 font-medium dark:border-white/10 dark:bg-black/10">
                {assignedVariantCount}/{variantAssignments.length} variant image assignments
              </span>
            </div>
          </div>

          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusPillClass(mediaOk)}`}>
            {images.length === 0
              ? "Needs attention • Add a cover image"
              : missingVariantCount > 0
                ? `Needs attention • ${missingVariantCount} variant image${missingVariantCount === 1 ? "" : "s"} missing`
                : `Complete • ${images.length} image${images.length === 1 ? "" : "s"} ready`}
          </span>
        </div>
      </div>

      <section className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Product Gallery
            </div>
            <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Drag to reorder. The first image becomes the cover image used across product listings.
            </div>
          </div>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusPillClass(images.length > 0)}`}>
            {images.length > 0 ? `Cover ready • ${images.length} image(s)` : "Add 1+ product image"}
          </span>
        </div>

        <div className="mt-4">
          <ImageDropzone
            meta={{ type: "product" }}
            onUploaded={addProductImages}
            multiple
            maxFiles={8}
            label="Drag and drop product images here, or click to upload"
          />
        </div>

        {images.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {images.map((src, idx) => {
              const isCover = idx === 0;
              const isDragging = dragFrom === idx;
              const isOver = dragOver === idx && dragFrom !== null && dragFrom !== idx;

              return (
                <div
                  key={`${src}-${idx}`}
                  draggable
                  onDragStart={() => setDragFrom(idx)}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOver(idx);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    move(dragFrom, idx);
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  className={
                    "group relative overflow-hidden rounded-3xl border bg-white shadow-sm transition dark:bg-[#151517] " +
                    (isDragging ? "opacity-60 " : "") +
                    (isOver ? "ring-2 ring-zinc-300 dark:ring-zinc-700 " : "") +
                    "border-zinc-200 dark:border-zinc-800"
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      openPreview({
                        title: isCover ? "Cover Image" : `Gallery Image ${idx + 1}`,
                        src,
                        description: isCover
                          ? "This image is currently used as the product cover."
                          : "Gallery image preview.",
                      })
                    }
                    className="relative block aspect-[4/5] w-full overflow-hidden bg-zinc-100 dark:bg-zinc-900"
                    title="Preview image"
                  >
                    <img src={getImageUrl(src)} alt="" className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0 opacity-0 transition group-hover:opacity-100" />
                    <span className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition group-hover:opacity-100">
                      <Expand size={16} />
                    </span>
                    {isCover && (
                      <div className="absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-white">
                        Cover
                      </div>
                    )}
                  </button>

                  <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {isCover ? "Primary storefront image" : "Gallery image"}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="rounded-xl border border-zinc-200 px-2.5 py-1.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-900"
                        onClick={() => setReplaceIdx(idx)}
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        className="rounded-xl border border-zinc-200 px-2.5 py-1.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-900"
                        onClick={() => removeImage(src)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {replaceIdx === idx && (
                    <div className="border-t border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-950/30">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
                          Replace image
                        </div>
                        <button
                          type="button"
                          className="text-[11px] text-zinc-500 underline dark:text-zinc-400"
                          onClick={() => setReplaceIdx(null)}
                        >
                          Cancel
                        </button>
                      </div>

                      <ImageDropzone
                        meta={{ type: "product", replaceIndex: idx }}
                        multiple={false}
                        maxFiles={1}
                        label="Drop or click to upload a replacement"
                        onUploaded={(urls) => {
                          const nextUrl = urls?.[0];
                          if (nextUrl) replaceAt(idx, nextUrl);
                          setReplaceIdx(null);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Variant Image Assignments
            </div>
            <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Assign or replace the image shown for each purchasable variant. This is now the single home for variant image management.
            </div>
          </div>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusPillClass(variantAssignments.length === 0 || missingVariantCount === 0)}`}>
            {variantAssignments.length === 0
              ? "No variants yet"
              : missingVariantCount === 0
                ? "All variants have images"
                : `${missingVariantCount} variant image${missingVariantCount === 1 ? "" : "s"} missing`}
          </span>
        </div>

        {variantAssignments.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400">
            Create variant combinations first. Once variants exist, assign each one its own image here.
          </div>
        ) : (
          <div className="mt-4 grid gap-4 xl:grid-cols-2">
            {variantAssignments.map(({ variantKey, label, imageUrl }) => {
              const hasImage = Boolean(imageUrl);
              const isReplacing = replaceVariantKey === variantKey;

              return (
                <div
                  key={variantKey}
                  className="overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between gap-3 bg-white px-4 py-2.5 dark:bg-[#151517]">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{label}</div>
                      <div className="truncate text-[11px] text-zinc-400 dark:text-zinc-500">{variantKey}</div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusPillClass(hasImage)}`}>
                      {hasImage ? "Assigned" : "Missing image"}
                    </span>
                  </div>

                  {/* Image area — shows image OR dropzone, never both simultaneously */}
                  <div className="bg-zinc-50 dark:bg-zinc-950/20">
                    {hasImage && !isReplacing ? (
                      /* Assigned image with hover overlay for actions */
                      <div className="group relative">
                        <button
                          type="button"
                          onClick={() =>
                            openPreview({
                              title: `${label} image`,
                              src: imageUrl,
                              description: "Variant image preview.",
                            })
                          }
                          className="block w-full"
                        >
                          <img
                            src={getImageUrl(imageUrl)}
                            alt={label}
                            className="aspect-[16/10] w-full object-cover transition duration-200 group-hover:brightness-75"
                          />
                        </button>
                        {/* Action overlay */}
                        <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-2 p-3 opacity-100 transition sm:pointer-events-none sm:opacity-0 sm:group-hover:pointer-events-auto sm:group-hover:opacity-100">
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => setReplaceVariantKey(variantKey)}
                              className="rounded-lg bg-white/90 px-2.5 py-1.5 text-[11px] font-medium text-zinc-800 shadow-sm backdrop-blur hover:bg-white"
                            >
                              Replace
                            </button>
                            {coverImage && imageUrl !== coverImage && (
                              <button
                                type="button"
                                onClick={() => setVariantImage(variantKey, coverImage)}
                                className="rounded-lg bg-white/90 px-2.5 py-1.5 text-[11px] font-medium text-zinc-800 shadow-sm backdrop-blur hover:bg-white"
                              >
                                Use cover
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setVariantImage(variantKey, null)}
                              className="rounded-lg bg-white/90 px-2.5 py-1.5 text-[11px] font-medium text-rose-600 shadow-sm backdrop-blur hover:bg-white"
                            >
                              Remove
                            </button>
                          </div>
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white">
                            <Expand size={14} />
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* No image — inline dropzone + use cover option */
                      <div className="p-4 space-y-3">
                        {isReplacing && hasImage && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Upload a replacement image</span>
                            <button
                              type="button"
                              onClick={() => setReplaceVariantKey("")}
                              className="text-[11px] font-medium text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        <ImageDropzone
                          multiple={false}
                          maxFiles={1}
                          meta={{ type: "variant", variantKey }}
                          label={hasImage ? "Drop or click to replace" : "Drop or click to upload variant image"}
                          onUploaded={(urls) => {
                            const nextUrl = urls?.[0];
                            if (nextUrl) setVariantImage(variantKey, nextUrl);
                            setReplaceVariantKey("");
                          }}
                        />
                        {coverImage && !hasImage && (
                          <button
                            type="button"
                            onClick={() => setVariantImage(variantKey, coverImage)}
                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 py-2.5 text-xs font-medium text-zinc-600 hover:border-zinc-400 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/40"
                          >
                            <ImageIcon size={14} />
                            Use product cover image instead
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <FormModal
        open={Boolean(previewState)}
        onClose={() => {
          setPreviewState(null);
          setPreviewBroken(false);
        }}
        title={previewState?.title || "Image Preview"}
        size="xl"
        footer={(
          <BtnSecondary
            type="button"
            onClick={() => {
              setPreviewState(null);
              setPreviewBroken(false);
            }}
          >
            Close
          </BtnSecondary>
        )}
      >
        <div className="space-y-3">
          <div className="overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/30">
            {previewState?.src && !previewBroken ? (
              <img
                src={getImageUrl(previewState.src)}
                alt=""
                className="max-h-[70vh] w-full object-contain"
                onError={() => setPreviewBroken(true)}
              />
            ) : (
              <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 px-6 text-center">
                {previewState?.src ? <ImageOff size={34} className="text-zinc-400 dark:text-zinc-500" /> : <ImageIcon size={34} className="text-zinc-400 dark:text-zinc-500" />}
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {previewState?.src
                    ? "This image could not be rendered."
                    : previewState?.emptyLabel || "No image assigned yet."}
                </div>
                <div className="max-w-lg text-xs text-zinc-500 dark:text-zinc-400">
                  {previewState?.src
                    ? "The stored image URL exists, but the browser could not decode it. Replace the asset if the problem persists."
                    : previewState?.description || "Upload or assign an image to preview it here."}
                </div>
              </div>
            )}
          </div>

          {previewState?.description && !previewBroken && (
            <div className="text-sm text-zinc-600 dark:text-zinc-400">{previewState.description}</div>
          )}
        </div>
      </FormModal>
    </div>
  );
}
